import { Checkmark, ChevronDown, Earth } from '@carbon/icons-react';
import { Modal, Popover, PopoverContent } from '@carbon/react';
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useBreakpoint } from '../../hooks';
import { cx } from '../../utils';
import styles from './CdLanguageSelect.module.scss';
import { LanguageFlag } from './languageFlags';

export interface LanguageOption {
  code: string;
  label: string;
}

export interface CdLanguageSelectProps {
  languages: readonly LanguageOption[];
  value?: string;
  onChange?: (code: string) => void;
  /** Accessible name for the control (also used as the mobile modal heading). */
  label?: string;
  /** `onDark` for navy surfaces such as the site footer. */
  tone?: 'default' | 'onDark';
  className?: string;
}

/** Language control: desktop popover, mobile flag + short code that opens a Carbon modal. */
export const CdLanguageSelect = ({
  languages,
  value,
  onChange,
  label = 'Language',
  tone = 'default',
  className,
}: CdLanguageSelectProps) => {
  const [open, setOpen] = useState(false);
  const isDesktop = useBreakpoint('md');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useId();
  const selected = languages.find((language) => language.code === value) ?? languages[0];

  const menuOpen = open && isDesktop;
  const modalOpen = open && !isDesktop;
  const options = () => [...(listRef.current?.querySelectorAll('button') ?? [])];

  useEffect(() => {
    if (!menuOpen) return;
    const buttons = options();
    const current = languages.findIndex((language) => language.code === selected?.code);
    buttons[current >= 0 ? current : 0]?.focus();
  }, [menuOpen, languages, selected?.code]);

  const close = (restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  };

  const choose = (code: string) => {
    onChange?.(code);
    close(true);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!menuOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const buttons = options();
    const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const step = event.key === 'ArrowDown' ? 1 : -1;
    buttons[(current + step + buttons.length) % buttons.length]?.focus();
  };

  const optionList = (
    <ul ref={listRef} id={listId} role="listbox" aria-label={label} className={styles.list}>
      {languages.map((language) => {
        const isSelected = language.code === selected?.code;
        return (
          <li key={language.code} role="none">
            <button
              type="button"
              role="option"
              lang={language.code}
              aria-selected={isSelected}
              className={cx(styles.option, isSelected && styles.selected)}
              onClick={() => choose(language.code)}
            >
              <LanguageFlag code={language.code} />
              <span className={styles.optionLabel}>{language.label}</span>
              <span className={styles.optionCode} aria-hidden="true">
                {language.code}
              </span>
              {isSelected && <Checkmark aria-hidden="true" className={styles.check} />}
            </button>
          </li>
        );
      })}
    </ul>
  );

  return (
    // Keyboard handling for the open desktop menu (Esc, arrows) lives on the wrapper so it covers
    // both the trigger and the options.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={cx(styles.language, tone === 'onDark' && styles.onDark, className)}
      onKeyDown={handleKeyDown}
    >
      {isDesktop ? (
        <>
          <Earth aria-hidden="true" className={styles.globe} />
          <Popover
            open={menuOpen}
            align="bottom-end"
            autoAlign
            caret={false}
            dropShadow
            onRequestClose={() => close(false)}
          >
            <button
              ref={triggerRef}
              type="button"
              className={styles.trigger}
              aria-label={label}
              aria-haspopup="listbox"
              aria-expanded={menuOpen}
              aria-controls={menuOpen ? listId : undefined}
              onClick={() => setOpen((isOpen) => !isOpen)}
            >
              {selected && <LanguageFlag code={selected.code} />}
              <span>{selected?.label}</span>
              <ChevronDown aria-hidden="true" className={styles.chevron} />
            </button>
            <PopoverContent className={styles.menu}>{optionList}</PopoverContent>
          </Popover>
        </>
      ) : (
        <>
          <button
            ref={triggerRef}
            type="button"
            className={styles.trigger}
            aria-label={label}
            aria-haspopup="dialog"
            aria-expanded={modalOpen}
            onClick={() => setOpen(true)}
          >
            {selected && <LanguageFlag code={selected.code} />}
            <span className={styles.code}>{selected?.code}</span>
          </button>
          <Modal
            open={modalOpen}
            passiveModal
            size="xs"
            modalHeading={label}
            aria-label={label}
            launcherButtonRef={triggerRef}
            onRequestClose={() => close(false)}
            className={styles.modal}
          >
            {optionList}
          </Modal>
        </>
      )}
    </div>
  );
};
